import React, { lazy, Suspense, useCallback, useContext, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Layout } from 'antd';
import GrowthAnalysis from 'components/pages/GrowthAnalysis';
import Navbar from 'components/organism/Navbar';
import Filter from 'components/organism/Filter';
import PromoScorecard from 'components/pages/PromoScorecard';
import BumpReport from 'components/pages/BumpReport';
import PromoLandscape from 'components/pages/PromoLandscape';
import Login from 'components/pages/Login';
import PortfolioAnalysis from 'components/pages/PortfolioAnalysis';
import GlobalProvider from 'contexts';
import PromoRecommendation from 'components/pages/PromoRecommendation';
import { PromoRecommendationContext } from 'contexts/PromoRecommendationContext';
import PriceLadder from 'components/pages/PriceLadder';
import OpportunityIdentification from 'components/pages/OportunityIdentification';
import PriceCurve from 'components/pages/PriceCurve';
import PriceChange from 'components/pages/PriceChange';
import PricePoint from 'components/pages/PricePoint';
import PriceTracking from 'components/pages/PriceTracking';
import { PriceSimulatorContext } from 'contexts/PriceSimulatorContext';
import { PricingSimulatorContext } from 'contexts/PricingSimulatorContext';
import PriceSimulator from 'components/pages/PriceSimulator';
import { ConfigContext, UserContext } from 'contexts/ConfigContext';
import { trackEventMixpanel } from 'utils/mixpanel';
import { LOGOUT_USER } from 'utils/mixpanelEventNames';
import MainLogo from 'assets/images/logo.png';
import PVMAnalysis from 'components/pages/PVMAnalysis';
import Depletion from 'components/pages/Depletion';
import axios from 'axios';
import { fetchTokenFromLocalStorage, handleTokenErrorAndRedirect } from 'utils/helperMethods';
import CommercialForecast from 'components/pages/CommercialForecast';
import { PricingSimulator } from 'components/pages';
import { UpdateOrViewPricingSimulator } from 'components/organism/PricingSimulator';
import UpdateOrViewPricingAnalytics from 'components/organism/PricingSimulator/UpdateOrViewPricingAnalytics';
import { PricingAnalyticsContext } from 'contexts/PricingAnalyticsContext';
import { CreatePriceSimulator } from 'components/organism';
import { TPOContext } from 'contexts/TPOContext';
import TPOPage from 'components/pages/TPOPage';
import UpdateOrViewTPO from 'components/organism/TradePromoOptimization/UpdateOrViewTPO';
import { PricingSimulatorWrapperContext } from 'contexts/PricingSimulatorWrapperContext';
import AppVersionRefresh from 'components/AppVersionRefresh';
import ProductFruitHelpdesk from 'components/molecules/ProductFruitHelpdesk';
import JiraComponent from 'components/organism/JiraComponent';
import Loader from 'components/molecules/Loader';

import BestWeeksSection from 'components/template/PromoRecommendation/BestWeeksSection';
import auth0 from 'auth0-js';
import useTokenRenewal from 'hooks/general/useTokenRenewal';
import ProfitPool from 'components/pages/ProfitPoolOverview/ProfitPool';
import ProfitPoolEvolution from 'components/pages/PPEvolution/ProfitPoolEvolution';
import GrowthMarginDrivers from 'components/pages/GrowthMarginDrivers/GrowthMarginDrivers';
import StrategicOpportunity from 'components/pages/StrategicOpportunity/StrategicOpportunity';
import GrowthMarginBridges from 'components/pages/GrowthMarginBridges/GrowthMarginBridges';
import { ProfitPoolOverviewContextProvider } from 'components/pages/ProfitPoolOverview/context/ProfitPoolOverviewContext';
import ShipmentGrowthAnalysis from 'components/pages/ShipmentGrowthAnalysis';
import ShipmentBumpReport from 'components/pages/ShipmentBumpReport';
import { ProfitPoolEvolutionContextProvider } from 'components/pages/PPEvolution/context/ProfitPoolEvolutionContext';
import { GrowthMarginDriversContextProvider } from 'components/pages/GrowthMarginDrivers/context/GrowthMarginDriversContext';
import { GrowthMarginBridgesProvider } from 'components/pages/GrowthMarginBridges/GrowthMarginBridgesContext';
import { isAdminUser } from 'constants/admin';
//DEFINE ALL APPLICATION ROUTES HERE
//WHENEVER A NEW ROUTE IS ADDED, ADD IT IN CONFIG ALSO (config keys:screens,screenDetailsWithRoutes )

const AdminSettings = lazy(() => import('components/pages/AdminSettings'));
const CacheManagement = lazy(() => import('components/pages/CacheManagement'));
const ForceRefresh = lazy(() => import('components/pages/ForceRefresh'));
const SimulatorValidation = lazy(() => import('components/pages/SimulatorValidation'));
const AppLightup = lazy(() => import('components/pages/AppLightup'));

//pageId-component mapping
const routeComponentMapping: any = {
    pvm: <PVMAnalysis />,
    ga: <GrowthAnalysis from="growth-analysis" />,
    sga: <ShipmentGrowthAnalysis />,
    br: <BumpReport from="bump-report" />,
    sbr: <ShipmentBumpReport />,
    da: <Depletion />,
    pcn: <PriceChange />,
    pp: <PricePoint />,
    pld: <PriceLadder />,
    pc: <PriceCurve />,
    pt: <PriceTracking />,
    oi: <OpportunityIdentification />,
    ps: <PromoScorecard />,
    pl: <PromoLandscape />,
    pa: <PortfolioAnalysis />,
    bw: <BestWeeksSection />,
    pr: (
        <PromoRecommendationContext>
            <PromoRecommendation />
        </PromoRecommendationContext>
    ),
    psr: <PriceSimulator />,
    psrNew: (
        <PriceSimulatorContext>
            <CreatePriceSimulator />
        </PriceSimulatorContext>
    ),
    psrView: (
        <PriceSimulatorContext>
            <CreatePriceSimulator />
        </PriceSimulatorContext>
    ),
    psrEdit: (
        <PriceSimulatorContext>
            <CreatePriceSimulator />
        </PriceSimulatorContext>
    ),
    cf: <CommercialForecast />,
    // v2
    psrV2: (
        <PricingSimulatorWrapperContext>
            <PricingSimulatorContext>
                <PricingSimulator />
            </PricingSimulatorContext>
        </PricingSimulatorWrapperContext>
    ),
    // v2
    psrV2View: (
        <PricingSimulatorWrapperContext>
            <PricingSimulatorContext>
                <UpdateOrViewPricingSimulator />
            </PricingSimulatorContext>
        </PricingSimulatorWrapperContext>
    ),
    // v2
    psrV2Update: (
        <PricingSimulatorWrapperContext>
            <PricingSimulatorContext>
                <UpdateOrViewPricingSimulator />
            </PricingSimulatorContext>
        </PricingSimulatorWrapperContext>
    ),
    // v2
    psa: (
        <PricingSimulatorWrapperContext>
            <PricingSimulatorContext>
                <PricingSimulator />
            </PricingSimulatorContext>
        </PricingSimulatorWrapperContext>
    ),
    // v2
    psaUpdate: (
        <PricingSimulatorWrapperContext>
            <PricingAnalyticsContext>
                <UpdateOrViewPricingAnalytics />
            </PricingAnalyticsContext>
        </PricingSimulatorWrapperContext>
    ),
    // v2
    psaView: (
        <PricingSimulatorWrapperContext>
            <PricingAnalyticsContext>
                <UpdateOrViewPricingAnalytics />
            </PricingAnalyticsContext>
        </PricingSimulatorWrapperContext>
    ),
    tpo: (
        <TPOContext>
            <TPOPage />
        </TPOContext>
    ),
    plan: (
        <TPOContext>
            <TPOPage />
        </TPOContext>
    ),
    viewUpdatePlan: (
        <TPOContext>
            <UpdateOrViewTPO />
        </TPOContext>
    ),
    bestWeeksTool: (
        <TPOContext>
            <TPOPage />
        </TPOContext>
    ),
    playbookTool: (
        <TPOContext>
            <TPOPage />
        </TPOContext>
    ),
    promoLibraryTool: (
        <TPOContext>
            <TPOPage />
        </TPOContext>
    ),
    eventLibraryTool: (
        <TPOContext>
            <TPOPage />
        </TPOContext>
    ),
    report: (
        <TPOContext>
            <TPOPage />
        </TPOContext>
    ),
    ppo: (
        <ProfitPoolOverviewContextProvider>
            <ProfitPool />
        </ProfitPoolOverviewContextProvider>
    ),
    ppevo: (
        <ProfitPoolEvolutionContextProvider>
            <ProfitPoolEvolution />
        </ProfitPoolEvolutionContextProvider>
    ),
    gmd: (
        <GrowthMarginDriversContextProvider>
            <GrowthMarginDrivers />
        </GrowthMarginDriversContextProvider>
    ),
    gmb: (
        <GrowthMarginBridgesProvider>
            <GrowthMarginBridges />
        </GrowthMarginBridgesProvider>
    ),
    adminIndex: <AdminSettings />,
    so: <StrategicOpportunity />,
    cm: <CacheManagement />,
    fr: <ForceRefresh />,
    sv: <SimulatorValidation />,
    al: <AppLightup />,
};

//AUTH0 WEB AUTH
const webAuth = new auth0.WebAuth({
    domain: process.env.REACT_APP_AUTH0_DOMAIN_URL ?? '',
    clientID: process.env.REACT_APP_AUTH0_CLIENT_ID ?? '',
    audience: process.env.REACT_APP_AUTH0_AUDIENCE ?? '',
    scope: 'openid profile email',
    responseType: 'token id_token',
    redirectUri: `${window.location.origin}/callback`,
});

export default function App() {
    const navigate = useNavigate();
    const route = useLocation();
    // //fetching new token when old token expires.
    // oktaAuth.tokenManager.on('expired', function (key, expiredToken) {
    //     oktaAuth.tokenManager.renew(key);
    // });
    const onLogout = useCallback(() => {
        trackEventMixpanel(LOGOUT_USER);
        webAuth.logout({ returnTo: window.location.origin });
        localStorage.clear();
    }, [webAuth]);

    //token renewal auth0
    useTokenRenewal({ webAuth });

    //to control visibility of JIRA support button and freshdesk widget
    useEffect(() => {
        const button = document.getElementById('jsd-widget') as HTMLElement;
        const freshdeskButton = document.getElementById('freshworks-container') as HTMLElement;
        //for freshdesk
        if (button && process.env.REACT_APP_JIRA_HELP !== 'true') {
            button.style.display = 'none';
            return;
        }

        //for jira
        if (button && route.pathname.length < 2) {
            button.style.visibility = 'hidden';
        } else if (button && process.env.REACT_APP_JIRA_HELP === 'true') {
            button.style.visibility = 'visible';
            //    button.style.display = 'block';
            button.style.bottom = '-8px';
            button.style.userSelect = 'none';
        }

        //for freshdesk
        if (freshdeskButton && process.env.REACT_APP_FRESHDESK_WIDGET_ID === 'null') {
            freshdeskButton.style.display = 'none';
            return;
        }
        if (route.pathname.length < 2 && freshdeskButton) {
            freshdeskButton.style.display = 'none';
        } else if (freshdeskButton && process.env.REACT_APP_FRESHDESK_WIDGET_ID !== 'null') {
            freshdeskButton.style.display = 'block';
            freshdeskButton.style.userSelect = 'none';
        }
    }, [route]);

    //this is to handle when user directly uses url/refreshes
    useEffect(() => {
        if (!route.pathname.includes('callback') && route.pathname !== '/') {
            return navigate('/', { state: { path: window.location.pathname } });
        }
    }, []);

    //to control visibility of JIRA support button and freshdesk widget
    useEffect(() => {
        const button = document.getElementById('jsd-widget') as HTMLElement;
        const freshdeskButton = document.getElementById('freshworks-container') as HTMLElement;
        //for freshdesk
        if (button && process.env.REACT_APP_JIRA_HELP !== 'true') {
            button.style.display = 'none';
            return;
        }

        //for jira
        if (button && route.pathname.length < 2) {
            button.style.visibility = 'hidden';
        } else if (button && process.env.REACT_APP_JIRA_HELP === 'true') {
            button.style.visibility = 'visible';
            //    button.style.display = 'block';
            button.style.bottom = '-8px';
            button.style.userSelect = 'none';
        }

        //for freshdesk
        if (freshdeskButton && process.env.REACT_APP_FRESHDESK_WIDGET_ID === 'null') {
            freshdeskButton.style.display = 'none';
            return;
        }
        if (route.pathname.length < 2 && freshdeskButton) {
            freshdeskButton.style.display = 'none';
        } else if (freshdeskButton && process.env.REACT_APP_FRESHDESK_WIDGET_ID !== 'null') {
            freshdeskButton.style.display = 'block';
            freshdeskButton.style.userSelect = 'none';
        }
    }, [route]);

    //fetch auth token and add in header
    useEffect(() => {
        axios.interceptors.request.use(function (config) {
            const token = fetchTokenFromLocalStorage();
            config.headers.Authorization = `Bearer ${token}`;
            return config;
        });

        //intercepting errors
        axios.interceptors.response.use(undefined, (error) => {
            if (typeof error.response?.data === 'object') return Promise.reject(error);
            const parsedError = JSON.parse(error?.response?.data || '{}');
            //condition to check if token is invalid
            //refresh page if token is invalid so that new token will be generated
            //this will redirect user to login page if session is expired
            handleTokenErrorAndRedirect(parsedError);

            return Promise.reject(error);
        });
    }, []);

    return (
        <div>
            <div>
                <Routes>
                    <Route
                        path="*"
                        element={
                            <ConfigContext>
                                <MainContainer onLogout={onLogout} />
                            </ConfigContext>
                        }
                    />
                    <Route
                        path="/"
                        element={
                            <ConfigContext>
                                <LoginContainer onLogout={onLogout} />
                            </ConfigContext>
                        }
                    />
                    <Route
                        path="/callback"
                        element={
                            <ConfigContext>
                                <LoginContainer onLogout={onLogout} />
                            </ConfigContext>
                        }
                    />
                </Routes>
            </div>
        </div>
    );
}

type LoginProps = {
    onLogout: Function;
};
//LOGIN COMPONENT
const LoginContainer = ({ onLogout }: LoginProps) => (
    <div className="h-full w-full">
        <Routes>
            <Route path="*" element={<Login onLogout={onLogout} webAuth={webAuth} />} />
        </Routes>
    </div>
);

// Path prefix for admin-only routes; non-admin users are redirected to home when visiting these
const ADMIN_PATH_PREFIX = '/admin';

//WHENEVER A NEW ROUTE IS ADDED, ADD IT IN CONFIG ALSO (config keys:screens,screenDetailsWithRoutes )
//ALL COMPONENTS EXCEPT LOGIN
const MainContainer = ({ onLogout }: any) => {
    const { appConfig } = useContext(UserContext);
    const location = useLocation();
    const navigate = useNavigate();

    // Redirect non-admin users away from admin routes (e.g. direct URL / refresh)
    useEffect(() => {
        if (location.pathname.startsWith(ADMIN_PATH_PREFIX) && !isAdminUser()) {
            const homePath = appConfig?.screenDetailsWithRoutes?.[0]?.path;
            navigate(homePath ? `/${homePath}` : '/', { replace: true });
        }
    }, [location.pathname, appConfig?.screenDetailsWithRoutes, navigate]);

    const showJiraBugReport: boolean = process.env.REACT_APP_JIRA_ENABLE_BUG_REPORT === 'true' ? true : false;

    // TODO: make it function
    const removeGF =
        location.pathname?.includes('pricing-simulator') ||
        location.pathname?.includes('pricing-analytics') ||
        location.pathname?.includes('tpo') ||
        location.pathname?.includes('admin') ||
        location.pathname?.includes('cache-management') ||
        location.pathname?.includes('simulator-validation');

    return (
        <GlobalProvider>
            {/* App version refresh Component*/}
            <AppVersionRefresh />
            <ProductFruitHelpdesk />
            <Layout className="h-full font-primary ">
                <Navbar logOut={onLogout} />
                <div>
                    {!removeGF && <Filter />}
                    <div className="fixed bottom-4 left-4 z-[10000] flex items-center gap-4">
                        <div className="global-filter-width flex items-center gap-3 justify-center">
                            <span className="text-fontGray font-medium font-xxs mb-[6px]"> Powered by </span>
                            <img style={{ height: 32 }} src={MainLogo} />
                            {/* <img style={{ width: '171px' }} src={PoweredByAsper} /> */}
                        </div>
                    </div>
                    {showJiraBugReport && (
                        <div className="fixed bottom-4 right-24 z-[10000]">
                            <JiraComponent />
                        </div>
                    )}
                    <div
                        id="main-container"
                        style={{ minHeight: 'calc(100vh - 100px)' }}
                        className={`m-4 main-screen-margin-top ${
                            removeGF ? ' ' : 'global-filter-margin-left main-width'
                        }`}
                    >
                        <Suspense
                            fallback={
                                <div className="w-full bg-white" style={{ minHeight: 'calc(100vh - 100px)' }}>
                                    <Loader size="default" />
                                </div>
                            }
                        >
                            <Routes>
                                {appConfig?.screenDetailsWithRoutes?.flatMap((screen: any) => {
                                    const baseRoute = (
                                        <Route
                                            key={screen.path}
                                            path={screen.path}
                                            element={routeComponentMapping[screen.pageId]}
                                        />
                                    );

                                    // Adding subRoutes if present
                                    const subRoutes = screen.subRoutes?.flatMap((subRoute: any) => (
                                        <Route
                                            key={`${screen.path}/${subRoute.suffix}`}
                                            path={`${screen.path}/${subRoute.suffix}`}
                                            element={routeComponentMapping[subRoute?.pageId]}
                                        />
                                    ));

                                    return [baseRoute, ...(subRoutes || [])];
                                })}
                                <Route
                                    path="*"
                                    element={<Navigate to={appConfig?.screenDetailsWithRoutes?.[0]?.path} />}
                                />
                            </Routes>
                        </Suspense>
                    </div>
                </div>
            </Layout>
        </GlobalProvider>
    );
};
